import { Check, Monitor, Moon, Sun } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../lib/cn';
import { THEME_GROUND, THEMES, useTheme, type ThemeChoice } from '../../lib/theme';
import { IconButton } from '../ui/Button';
import { Sheet } from '../ui/Sheet';

const SWATCH_INK: Record<Exclude<ThemeChoice, 'system'>, string> = {
  light: '#1c1d21',
  sepia: '#33291f',
  dark: '#ebeae6',
  navy: '#f9a3c0',
};

/** Picks the colour theme for the whole app. Each option shows its own colours. */
export function ThemePicker({ onPicked }: { onPicked?: () => void }) {
  const { choice, setChoice } = useTheme();
  return (
    <div role="radiogroup" aria-label="Theme" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {THEMES.map((theme) => {
        const selected = theme.value === choice;
        const swatch =
          theme.value === 'system'
            ? `linear-gradient(135deg, ${THEME_GROUND.light} 50%, ${THEME_GROUND.dark} 50%)`
            : THEME_GROUND[theme.value];
        return (
          <button
            key={theme.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => {
              setChoice(theme.value);
              onPicked?.();
            }}
            className={cn(
              'flex items-center gap-3 rounded-xl p-2.5 text-left transition-colors',
              selected ? 'bg-accent-soft ring-2 ring-accent' : 'bg-sunken hover:bg-raised',
            )}
          >
            <span
              aria-hidden
              className="flex size-9 shrink-0 items-center justify-center rounded-lg font-serif text-small font-semibold ring-1 ring-black/10"
              style={{
                background: swatch,
                color: theme.value === 'system' ? 'transparent' : SWATCH_INK[theme.value],
              }}
            >
              Aa
            </span>
            <span className="flex-1 font-medium">{theme.label}</span>
            {selected && <Check className="size-4 text-accent" aria-hidden />}
          </button>
        );
      })}
    </div>
  );
}

/** The appearance button in the app bar, which opens the theme picker. */
export function ThemeButton() {
  const { choice, applied } = useTheme();
  const [open, setOpen] = useState(false);
  const Icon =
    choice === 'system' ? Monitor : applied === 'dark' || applied === 'navy' ? Moon : Sun;

  return (
    <>
      <IconButton label="Appearance" onClick={() => setOpen(true)}>
        <Icon />
      </IconButton>
      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title="Appearance"
        description="Applies everywhere, including the reader."
      >
        <ThemePicker onPicked={() => setOpen(false)} />
      </Sheet>
    </>
  );
}
